import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, Unsubscribable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IConfirmModalComponentData } from '../../shared/modals/confirm/confirm-modal.component';
import { Router } from '@angular/router';
import { IGroupData, IRowData } from '../../shared/models/list-models';
import { ApiService } from '../../shared/services/api-service/api-service';
import { IUser } from '../../shared/typings/interfaces/user.interface';
import { ModalsService } from '../../shared/modals/modals.service';
import { ShelterNetworkDataService } from './shelter-network.data.service';
import {
  IShelterClusterUserIdentifier,
  IShelterClusterUserModalShellData,
  TShelterClusterUserModalViewMode,
} from '../../shared/modals/shelter-user-management/shelter-cluster-user/shelter-cluster-user-modals.config';
import { unsubscribeComponent } from '../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';

@Component({
  selector: 'shelter-network-settings',
  template: require('./shelter-network-settings.component.html'),
  styles: [require('./shelter-network-settings.component.scss')],
})
@unsubscribeComponent
export class ShelterNetworkSettingsComponent implements OnInit {
  public shelterClusterName: string;
  public shelterClusterId: string;
  public sidebarSelection: string;
  public columns: string[] = [
    'SHELTER_USER_NAME',
    'SHELTER_JOB_TITLE',
    'SHELTER_ACCESS_LEVEL',
    'SHELTER_PORTFOLIO',
    'SHELTER_MORE',
  ];
  public groupData$: BehaviorSubject<IGroupData> = new BehaviorSubject(null);
  public networkName: string = 'User permissions';
  public usersSub: Unsubscribable;
  @Input() currentUser: IUser;

  constructor(
    private router: Router,
    private dataService: ShelterNetworkDataService,
    private apiService: ApiService,
    private modalsService: ModalsService,
  ) {}

  ngOnInit(): void {
    this.getShelterClusterUsers();
  }

  private getShelterClusterUsers(): void {
    this.usersSub = this.dataService
      .getUsers(this.columns)
      .pipe(
        tap((users: any) => {
          const { groupData } = users;
          this.groupData$.next(groupData);
        }),
      )
      .subscribe();
  }

  public onBack(route: string): void {
    this.updateUrl([route]);
  }

  private updateUrl(route: string[], extras = {}): void {
    this.router.navigate(route, extras);
  }

  public onClickedMoreButton({
    action,
    user,
  }: {
    action: 'DETAILS' | 'PORTFOLIO' | 'REMOVE';
    user: IShelterClusterUserIdentifier;
  }): void {
    switch (action) {
      case 'DETAILS':
        this.sendToEditUser({ user });
        break;
      case 'PORTFOLIO':
        this.sendToEditPortfolio({ user });
        break;
      case 'REMOVE':
        this.sendToRemoveUser({ user });
      default:
        break;
    }
  }
  public onClickedAddUser(): void {
    this.sendToCreateUser();
  }

  private sendToEditUser({ user }: { user: IShelterClusterUserIdentifier }): void {
    const mode = 'EDIT';
    const data: IShelterClusterUserModalShellData = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      user,
      mode,
    };
    this.modalsService
      .openShelterClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private sendToEditPortfolio({ user }: { user: IShelterClusterUserIdentifier }): void {
    const mode = 'PORTFOLIO';
    const data: IShelterClusterUserModalShellData = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      user,
      mode,
    };
    this.modalsService
      .openShelterClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private sendToRemoveUser({ user }): void {
    const data: IConfirmModalComponentData = {
      title: 'Remove User',
      message: `Are you sure you want to remove ${user.name} from this network?`,
      confirmText: 'Remove',
    };
    this.modalsService
      .openConfirmModal(data)
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          const userPayload = { userId: user.id };
          const columns = this.columns;
          const mode: TShelterClusterUserModalViewMode = 'REMOVE';
          this.apiService
            .mutateShelterClusterUser({ userPayload, columns }, mode)
            .pipe(
              tap((res: { data: { [mutationName: string]: IRowData[][] }; errors: any[] }) => {
                const { data } = res;
                if (data && Object.keys(data).length) {
                  this.onMutateUserReqReturned(data.deleteShelterClusterUser);
                }
              }),
            )
            .subscribe();
        }
      });
  }

  private sendToCreateUser(): void {
    const mode = 'CREATE';
    const data = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      mode,
    };
    this.modalsService
      .openShelterClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private onMutateUserReqReturned(res): void {
    const { groupData } = res;
    this.groupData$.next(groupData);
  }
}
